import * as React from 'react'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import VariationDetail from './VariationDetail'
import ClinvarDiagram from './ClinvarDiagram'
import GeneDetail from './GeneDetail'
import RelatedLiterature from './RelatedLiterature'
import { useTranslation } from 'react-i18next'
import ACMGDetail from './ACMGDetail'
import HGMDDetail from './HGMDDetail'
import HealthConditionsDetail from './HealthConditionsDetail'
import ClinicalSynopsisDetail from './ClinicalSynopsisDetail'
import { useRouter } from 'next/router'
import { useMutation, useQuery } from 'react-query'
import {
  analyzeArticle,
  AnalyzeArticleDto,
  analyzeVariant,
  AnalyzeVariantDto,
  fetchAnalysisVariantDetails,
  fetchVariantDetails,
  getArticles,
  GetArticlesDto,
  getExpression,
  getGoogleArticles,
  getLitVarArticles,
  getVarArticles,
  GetVariantLiteratureDto,
  IVariantDetailClinvarGraph,
  VariantDetail,
  VariantDetailHGMDInfo
} from '@/@core/services/app.service'
import Prediction from './Prediction'
import {
  Backdrop,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemProps,
  ListItemText,
  Stack,
  styled,
  TextField,
  Tooltip
} from '@mui/material'
import { DatabaseArrowDown } from 'mdi-material-ui'
import AnalyzeVariants from './AnalyzeVariant'
import GeneExpression from './GeneExpression'
import { isWindow } from '@/@core/utils/utils'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

// ** Styled Components
const ListItemStyled = styled(ListItem)<
  ListItemProps & { component?: React.ElementType }
>(({ theme }) => ({
  borderLeftWidth: '3px',
  borderLeftStyle: 'solid',
  padding: theme.spacing(0, 2),
  marginBottom: theme.spacing(1)
}))

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

const VariationGene = React.forwardRef(
  (
    props: {
      row: any
      analysisInfo: any
      analyzeVariants: any
      setAnalyzeVariant: any
      setIsFinish: any
    },
    ref
  ) => {
    const { t } = useTranslation()
    const leftData = [
      {
        value: null,
        title: t('Allele frequency')
      },
      {
        value: [] as
          | {
              key: string
              value: any
            }[]
          | [],
        title: t('Prediction')
      },
      {
        value: [] as IVariantDetailClinvarGraph[] | [],
        title: t('Clinvar Graph')
      },
      {
        value: [] as IVariantDetailClinvarGraph[] | [],
        title: t('Gene Expression')
      }
    ]
    const [leftDataValue, setLeftDataValue] = React.useState(leftData)
    const [selectedIndex, setSelectedIndex] = React.useState(0)
    const [articles, setArticles] = React.useState<any>()
    const [GoogleArticles, setGoogleArticle] = React.useState<any>()
    const [expression, setExpression] = React.useState<any>()
    const [litVarArticles, setLitVarArticles] = React.useState<any>()
    const [variantsArticles, setVariantsArticles] = React.useState<any>()
    const [open, setOpen] = React.useState<boolean>(false)
    const [analyzeArticles, setAnalyzeArticles] = React.useState<any>()
    const [analyzeVariants, setAnalyzeVariant] = React.useState<any>()
    const [variantDetail, setVariantDetails] = React.useState<VariantDetail>()
    const [litPage, setLitPage] = React.useState(1)
    const router = useRouter()

    const { id } = router.query

    const type = router.asPath.includes('/samples') ? 'sample' : 'analyze'

    const { data } = useQuery<VariantDetail>(
      ['variantDetails'],
      async () => {
        const variants = {
          chrom: props.row.chrom,
          pos: props.row.pos,
          REF: props.row.REF ?? props.row.ref,
          ALT: props.row.ALT ?? props.row.alt,
          gene: props.row.gene,
          genomeBuild: props.analysisInfo.genomeBuild
        }

        if (type === 'sample')
          return await fetchVariantDetails({
            sampleId: id as unknown as number,
            variants
          })
        else {
          const analysisItemType = localStorage.getItem('analysisItemType')

          return await fetchAnalysisVariantDetails({
            analysisId: id as unknown as number,
            variants: {
              ...variants,
              analysisItemType
            }
          })
        }
      },
      {
        keepPreviousData: true,
        onSuccess: (variantDetails) => {
          if (props.analysisInfo.pipelineVersion) {
            setVariantDetails({
              ...variantDetails,
              variantInfo: props.row
            })
          } else {
            setVariantDetails(variantDetails)
          }
          const variantInfo = variantDetails.variantInfo ?? props.row
          const Prediction = Object.getOwnPropertyNames(variantInfo)
            .map((key) => {
              if (
                key === 'CADD_phred' ||
                key === 'cadd_phred' ||
                key === 'f_score' ||
                key === 'g_score' ||
                key === 'REVEL_score' ||
                key === 'revel' ||
                key === 'SpliceAI_max_score' ||
                key === 'spliceai_max_score' ||
                key === 'AM_pathogenicity' ||
                key === 'am_pathogenicity' ||
                key === 'SIFT_score' ||
                key === 'sift' ||
                key === 'polyphen2_HDIV_score' ||
                key === 'polyphen'
              ) {
                let value = variantInfo[key as 'f_score']
                if (key === 'f_score')
                  value = (value * 100).toFixed(2) as unknown as number
                if (key === 'g_score')
                  value = (value * 100).toFixed(2) as unknown as number
                let newkey = key
                if (key === 'f_score') newkey = 'F score'
                if (key === 'g_score') newkey = 'G score'
                if (key === 'REVEL_score') newkey = 'Revel'
                if (key === 'revel') newkey = 'Revel'
                if (key === 'AM_pathogenicity')
                  newkey = 'AlphaM (pathogenicity)'
                if (key === 'am_pathogenicity')
                  newkey = 'AlphaM (pathogenicity)'
                if (key === 'SpliceAI_max_score') newkey = 'SpliceAI (max.)'
                if (key === 'spliceai_max_score') newkey = 'SpliceAI (max.)'
                if (key === 'SIFT_score') newkey = 'SIFT score'
                if (key === 'sift') newkey = 'SIFT score'
                if (key === 'polyphen2_HDIV_score') newkey = 'Polyphen2'
                if (key === 'polyphen') newkey = 'Polyphen2'
                if (key === 'CADD_phred') newkey = 'CADD_PHRED'
                if (key === 'cadd_phred') newkey = 'CADD_PHRED'

                return {
                  key: newkey,
                  value
                }
              }
            })
            .filter((i) => i) as { key: string; value: any }[]
          const arrName: string[] = []

          Prediction.forEach((el) => {
            arrName.push(el.key)
          })

          if (arrName.indexOf('G score') > -1) {
            const filterArr = Prediction.splice(arrName.indexOf('g_score'), 1)

            Prediction.splice(0, 0, filterArr[0])
          }

          if (arrName.indexOf('F score') > -1) {
            const filterArr = Prediction.splice(arrName.indexOf('f_score'), 1)

            Prediction.splice(0, 0, filterArr[0])
          }

          // gtexData.mutateAsync(props.row.gene)
          const rsId =
            props.row.rs_id ||
            (props.row.rsid && Array.isArray(props.row.rsid)
              ? props.row.rsid[0]
              : '')
          const params = {
            gene: props.row.gene,
            rsId: rsId,
            pnomen: props.row.p_nomen ?? props.row.hgvsc,
            cnomen: props.row.c_nomen ?? props.row.hgvsp,
            pubmed: `${props.row.pubmed}`,
            omimPubmed: props.row.OMIM?.pubmedIds
          }

          if (props.row.MitoMap) {
            params.omimPubmed = props.row.MitoMap.pubmed_ids
          }
          if (props.row.MitoMap) {
            params.omimPubmed = props.row.MitoMap.pubmed_ids
          }
          if (
            props.row.pubmed_ids &&
            Array.isArray(props.row.pubmed_ids) &&
            props.row.pubmed_ids.length
          ) {
            params.omimPubmed = props.row.pubmed_ids.join(',')
          }

          // if (props.row.LitVarArticles) {
          //   setLitVarArticles(props.row.LitVarArticles)
          // }

          // if (props.row.Articles) {
          //   setArticles(props.row.Articles)
          // }
          if (params.omimPubmed) {
            Articles.mutateAsync(params)

            GoogleArticle.mutateAsync(params)
          }

          if (rsId && rsId !== '.')
            getLitVarArticle.mutateAsync({
              rsId: rsId,
              litPage
            })

          const variantsParams = {
            assembly: props.analysisInfo.genomeBuild,
            variant: `${props.row.chrom}-${props.row.pos}-${props.row.REF}-${props.row.ALT}`
          }

          // if (variantsParams.assembly) getVarArticle.mutateAsync(variantsParams)

          // const analyzeVariantParams = {
          //   variantInfo: `${props.row.gene},${props.row.c_nomen},${props.row.p_nomen},${props.row.rs_id}`
          // }

          // AnalyzeVariant.mutateAsync(analyzeVariantParams)

          setLeftDataValue([
            {
              value: null,
              title: t('Allele frequency')
            },
            {
              value: Prediction,
              title: t('Prediction')
            },
            {
              value: variantDetails.clinvarGraph,
              title: t('Clinvar Graph')
            },
            {
              value: variantDetails.clinvarGraph,
              title: t('Gene Expression')
            }
          ])
        }
      }
    )

    const handleClickLeft = (index: number) => {
      //

      setSelectedIndex(index)
      const container = document.getElementById('scroll')
      const chptListContainer = document.getElementById('chpt_list')

      let height = chptListContainer?.scrollHeight ?? 0

      for (let i = 0; i < index; i++) {
        height += document.getElementById(`${i}`)?.scrollHeight as number
      }

      container?.scrollTo({
        top: height
      })
    }

    const handleClose = () => {
      setOpen(false)
    }

    const handleOpen = (value: boolean) => {
      setOpen(value)
    }
    const GoogleArticle = useMutation({
      mutationFn: async (data: GetArticlesDto) => await getGoogleArticles(data),
      onSuccess: (datas: any) => {
        //post返回的结果
        // setOMIM(datas)
        setGoogleArticle(datas)
      }
    })

    const Articles = useMutation({
      mutationFn: async (data: GetArticlesDto) => await getArticles(data),
      onSuccess: (datas: any) => {
        //post返回的结果

        // const analyzeArticleParams = {
        //   title: '',
        //   abstract: ''
        // }

        // AnalyzeArticle.mutateAsync(analyzeArticleParams)
        setArticles(datas)
      }
    })

    const AnalyzeVariant = useMutation({
      mutationFn: async (data: AnalyzeVariantDto) => await analyzeVariant(data),
      onSuccess: (datas: any) => {
        //post返回的结果

        setAnalyzeVariant(datas)
      }
    })

    const gtexData = useMutation({
      mutationFn: async (gene: string) => await getExpression(gene),
      onSuccess: (datas: any) => {
        //post返回的结果

        setExpression(datas)
      }
    })

    const getLitVarArticle = useMutation({
      mutationFn: async (params: { rsId: string; litPage: number }) =>
        await getLitVarArticles(params.rsId, params.litPage),
      onSuccess: (datas: any) => {
        //post返回的结果

        setLitVarArticles(datas)
      }
    })

    const totalArticles = React.useMemo(() => {
      let obj = {} as any

      if (articles) {
        obj = {
          ...obj,
          ...articles
        }
      }

      if (GoogleArticles) {
        obj = {
          ...obj,
          ...GoogleArticles
        }
      }

      if (variantsArticles) {
        obj = {
          ...obj,
          ...variantsArticles
        }
      }

      return obj
    }, [articles, GoogleArticles, variantsArticles])

    React.useImperativeHandle(ref, () => {
      return {
        handleClickLeft
      }
    })

    return (
      <Box sx={{ height: '65%', width: '100%', backgroundColor: 'white' }}>
        <Grid container height={'100%'}>
          {isWindow() && (
            <>
              <Grid item xs={1.5} height={'100%'} position={'sticky'} top={0}>
                <List component="div">
                  {leftDataValue.map((item, index) => {
                    return (
                      <React.Fragment>
                        <ListItemStyled
                          sx={{
                            borderLeftColor:
                              index === selectedIndex
                                ? 'primary.main'
                                : 'transparent',
                            cursor: 'pointer'
                          }}
                          key={item.title}
                          onClick={() => {
                            handleClickLeft(index)
                          }}
                        >
                          <ListItemText
                            primary={item.title}
                            primaryTypographyProps={{
                              noWrap: true,
                              sx: {
                                fontWeight: 500,
                                ...(index === selectedIndex && {
                                  color: 'primary.main'
                                })
                              }
                            }}
                          />
                        </ListItemStyled>
                      </React.Fragment>
                    )
                  })}
                </List>
              </Grid>

              <Divider orientation="vertical" flexItem />
            </>
          )}

          <Grid item xs={isWindow() ? 10.4 : 12} padding={2} height="100%">
            <Box
              overflow={'auto'}
              maxHeight={'100%'}
              onScroll={(e: any) => {
                const scrollTop = e.target.scrollTop as number

                // let index = 0

                leftDataValue.reduce((acc, cur, index) => {
                  const temp = document.getElementById(`${index}`)
                    ?.clientHeight as number

                  const a = scrollTop - acc

                  const b = acc + temp - scrollTop

                  if (a < b && a > 0) {
                    setSelectedIndex(index)
                  } else if (a > b && a > 0) {
                    setSelectedIndex(index + 1)
                  }

                  return acc + temp
                }, 0)
              }}
            >
              <Grid item xs={12} id={'chpt_list'}>
                {props.analyzeVariants ? (
                  <AnalyzeVariants
                    setAnalyzeVariant={props.setAnalyzeVariant}
                    setIsFinish={props.setIsFinish}
                    value={props.analyzeVariants}
                  />
                ) : (
                  ''
                )}
              </Grid>

              {variantDetail
                ? leftDataValue.map((items, index) => {
                    return (
                      <Box key={items.title} id={`${index}`}>
                        <Box paddingTop={2}>
                          {items.title === t('Allele frequency') ? (
                            <VariationDetail
                              value={variantDetail}
                              key={variantDetail?.geneMim}
                              handleOpen={handleOpen}
                              analysisInfo={props.analysisInfo}
                              row={props.row}
                              analyzeVariants={props.analyzeVariants}
                            />
                          ) : items.title === t('Internal frequency') ? (
                            ''
                          ) : items.title === t('Prediction') ? (
                            <HGMDDetail
                              predictionValuevalue={
                                leftDataValue[index].value as any
                              }
                            />
                          ) : items.title === t('Clinvar Graph') ? (
                            <ClinvarDiagram
                              value={leftDataValue[index].value as any}
                            />
                          ) : items.title === t('Gene Expression') ? (
                            <GeneExpression
                              value={expression ?? []}
                              gene={props.row.gene}
                              vatiantInfo={variantDetail?.variantInfo}
                            />
                          ) : items.title === t('Clinical evidence') ? (
                            ''
                          ) : items.title === t('Publication') ? (
                            <RelatedLiterature
                              value={totalArticles ?? {}}
                              analysisInfo={props.analysisInfo}
                              row={props.row}
                              handleOpen={handleOpen}
                              litVarArticles={litVarArticles}
                              setLitPage={setLitPage}
                              page={litPage}
                              getLitVarArticle={getLitVarArticle}
                              hgmdValue={variantDetail.hgmd as any}
                            />
                          ) : (
                            ''
                          )}
                        </Box>
                      </Box>
                    )
                  })
                : ''}
            </Box>
          </Grid>
        </Grid>

        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={open}
          onClick={handleClose}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
    )
  }
)

export default VariationGene
